import {
  Component,
  DOCUMENT,
  DestroyRef,
  HostBinding,
  HostListener,
  Injector,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Intercom } from '@supy-io/ngx-intercom';
import { Angulartics2GoogleAnalytics } from 'angulartics2';
import { debounce } from 'lodash';
import moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { LocalStorageService } from '@core/providers/storage/local-storage.service';
import { FoodbackLoaderComponent } from '@shared/components/foodback-loader/foodback-loader.component';
import { ServerEnvironmentConfig } from './core/interfaces/server-environment.interface';
import { AccountAnalyticsBackendService } from './core/providers/backend/account-analytics-backend.service';
import { GoogleAnalyticsService } from './core/providers/backend/google-analytics.service';
import { IntercomService } from './core/providers/backend/intercom.service';
import { UsersBackendService } from './core/providers/backend/users-backend.service';
import { VenueAnalyticsBackendService } from './core/providers/backend/venue-analytics-backend.service';
import { ContextService } from './core/providers/context/context.service';
import { PrintPageService } from './core/providers/print/print-page.service';
import { ServerEnvironmentService } from './core/providers/server-environment/server-environment.service';
import { ThemeService } from './core/providers/theme/theme.service';
import { UserService } from './core/providers/user/user.service';
import { LayoutUtil } from './core/utils';
import { LastUrlStore } from './features/login/last-url.store';
import { DateRangePickerService } from './shared/components/date-range-picker/date-range-picker.service';
import { CONSTANTS } from './shared/constants';
import { ContextObject } from './shared/interfaces/login-object';
import { isResumableUrl } from './shared/utils/is-resumable-url';

/**
 * Distance from the corner for the Intercom launcher. Intercom positions its launcher itself, from
 * these boot settings; on a compact viewport global.scss scales it into the corner to match the
 * shell's navigation launcher on the opposite side.
 */
const INTERCOM_LAUNCHER_INSET = 20;

/**
 * The theme owns the brand colour; this reads it rather than repeating the hex. It reads the fixed
 * brand navy rather than `primary`, for two reasons: Intercom draws a white glyph on whatever it is
 * given, and primary lightens to a pale lavender in the dark theme; and Intercom boots once, so a
 * value that follows the theme would go stale the moment someone switched it.
 */
function brandPrimaryColor(): string {
  return getComputedStyle(document.documentElement).getPropertyValue('--fb-brand-navy').trim();
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet, FoodbackLoaderComponent],
})
export class AppComponent implements OnInit {
  private document = inject(DOCUMENT);
  private intercomService = inject(IntercomService);
  private serverEnvironment = inject(ServerEnvironmentService);
  private layoutUtil = inject(LayoutUtil);
  private printPageService = inject(PrintPageService);
  private themeService = inject(ThemeService);
  private contextService = inject(ContextService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private dateRangePickerService = inject(DateRangePickerService);
  private accountAnalyticsBackendService = inject(AccountAnalyticsBackendService);
  private venueAnalyticsBackendService = inject(VenueAnalyticsBackendService);
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);
  private usersBackendService = inject(UsersBackendService);
  private localStorage = inject(LocalStorageService);
  private lastUrlStore = inject(LastUrlStore);
  private userService = inject(UserService);
  private injector = inject(Injector);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  @HostBinding('class.aside-collapsed') isCollapsed = false;
  @HostBinding('class.aside-expanded') isExpanded = false;
  @HostListener('window:resize') onResize = debounce(() => this.handleSidebarOnPageResize(), 100);
  isLoading = true;

  private isMobileDevice = this.layoutUtil.isMobileDevice();

  private userActivitySubscription: Subscription = new Subscription();
  private routerInstance?: Router;

  constructor() {
    this.isCollapsed = false;
  }

  ngOnInit(): void {
    this.themeService.init();
    this.syncDocumentLanguage();
    this.initAngulartics();
    this.initIntercom();
    this.loadIcons();
    this.handleRouterEvents();
    this.printPageService.start();
    this.handleSidebarChanges();
    this.handleResponsiveStateEvents();
    this.googleAnalyticsService.injectGAScript();
    this.handleContextAndSetFirstFoodbackDate();
  }

  private initAngulartics(): void {
    // Start tracking after bootstrap to avoid triggering Router creation during AppComponent DI.
    queueMicrotask(() => {
      try {
        const ga = this.injector.get(Angulartics2GoogleAnalytics);
        ga.startTracking();
      } catch {}
    });
  }

  private syncDocumentLanguage(): void {
    this.setDocumentLanguage(
      this.translateService.currentLang ||
        this.translateService.getDefaultLang() ||
        this.document.documentElement.lang ||
        'en-GB'
    );

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.setDocumentLanguage(event.lang));
  }

  private setDocumentLanguage(language: string | undefined): void {
    const normalizedLanguage = language?.trim();

    this.document.documentElement.lang = normalizedLanguage || 'en-GB';
  }

  private handleContextAndSetFirstFoodbackDate(): void {
    this.getContext$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((context: ContextObject) => this.setInitialFirstFoodbackDate(context));
  }

  private setInitialFirstFoodbackDate(context: ContextObject): void {
    if (context.uuid === undefined) {
      return;
    }

    const analyticsService =
      context.type === CONSTANTS.CONTEXT.ACCOUNT
        ? this.accountAnalyticsBackendService
        : this.venueAnalyticsBackendService;

    analyticsService.getFoodbackFirstDate$(context.uuid).subscribe(date => {
      if (date.firstFoodbackDate) {
        this.setFirstFoodbackDate(date.firstFoodbackDate);
      }
    });
  }

  private handleResponsiveStateEvents(): void {
    this.getIsMobileDevice$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: boolean) => (this.isMobileDevice = data));

    this.getIsExpanded$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: boolean) => (this.isExpanded = data));
  }

  private handleSidebarChanges(): void {
    this.getIsSidebarCollapsed$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: boolean) => {
        this.isCollapsed = data && !this.isMobileDevice;
        this.intercomService.updateIntercomData({
          hide_default_launcher: !data && this.isMobileDevice,
        });
      });
  }

  private handleRouterEvents(): void {
    this.getRouterEvents$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: Event) => {
        if (
          (data instanceof NavigationEnd ||
            data instanceof NavigationCancel ||
            data instanceof NavigationError) &&
          this.isLoading
        ) {
          this.isLoading = false;
        }
        if (data instanceof NavigationEnd) {
          if (isResumableUrl(data.urlAfterRedirects)) {
            this.lastUrlStore.remember(data.urlAfterRedirects);
          }

          this.setAndVerifyLastActivity();
        }
      });
  }

  private initIntercom(): void {
    // Delay Intercom creation until after bootstrap to avoid a Router DI cycle,
    // and until we have the app_id from the backend configuration.
    this.getServerEnvironmentConfig$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(config => {
        const app_id = config?.intercom?.appId;

        if (!app_id) {
          return;
        }

        const intercom = this.injector.get(Intercom);

        intercom.boot({
          app_id,
          // The same inset on both axes, which global.scss relies on to bring it to the
          // navigation launcher's corner on a compact viewport.
          alignment: 'right',
          horizontal_padding: INTERCOM_LAUNCHER_INSET,
          vertical_padding: INTERCOM_LAUNCHER_INSET,
          // Deprecated by Intercom in favour of the workspace's own Messenger styling, which wins
          // where it is set. Passed anyway so the launcher follows the brand colour on workspaces
          // that still honour it, and read from the theme so there is one source for the value.
          action_color: brandPrimaryColor(),
          background_color: brandPrimaryColor(),
          // widget: {
          //   activator: '#intercom',
          // },
        });

        this.intercomService.initIntercom(intercom);
        this.intercomService.updateIntercomData({ app_id });
      });
  }

  private setFirstFoodbackDate(date: string): void {
    this.dateRangePickerService.minDateRange = moment(date).toDate();
  }

  private getServerEnvironmentConfig$(): Observable<Readonly<ServerEnvironmentConfig>> {
    return this.serverEnvironment.getServerEnvironmentConfig$();
  }

  private getContext$(): Observable<ContextObject> {
    return this.contextService.getContext$();
  }

  private getIsMobileDevice$(): Observable<boolean> {
    return this.layoutUtil.getIsMobileDevice$();
  }

  private getRouterEvents$(): Observable<Event> {
    return this.getRouter().events;
  }

  private getRouter(): Router {
    if (!this.routerInstance) {
      this.routerInstance = this.injector.get(Router);
    }

    return this.routerInstance;
  }

  private getIsExpanded$(): Observable<boolean> {
    return this.layoutUtil.getIsSidebarExpanded$();
  }

  private getIsSidebarCollapsed$(): Observable<boolean> {
    return this.layoutUtil.getIsCollapsed$();
  }

  private handleSidebarOnPageResize(): void {
    const wasMobile: boolean = this.layoutUtil.isMobileDevice();
    this.layoutUtil.setMobileDeviceState();

    const isMobileNow = this.layoutUtil.isMobileDevice();

    if (wasMobile && !isMobileNow) {
      // Default to expanded when coming back to desktop.
      this.layoutUtil.setIsCollapsed(false);
    }
  }

  private loadIcons(): void {
    this.matIconRegistry.addSvgIcon(
      'foodback_intelligence',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg/foodback-intelligence.svg')
    );
    // The rail's Copilot glyph: drawn in currentColor, so it follows a tinted tile in both themes.
    this.matIconRegistry.addSvgIcon(
      'copilot',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg/icon-copilot.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'social-facebook',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg/social-media/facebook.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'social-instagram',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg/social-media/instagram.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'social-linkedin',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg/social-media/linkedin.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'social-x',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg/social-media/x.svg')
    );
    this.matIconRegistry.addSvgIcon(
      'social-youtube',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg/social-media/youtube.svg')
    );
  }

  private setAndVerifyLastActivity(): void {
    if (!this.userService.getUserUuid()) {
      return;
    }
    const lastActivity: number = this.localStorage.retrieve(CONSTANTS.SESSION.LAST_ACTIVITY);

    if (!lastActivity) {
      this.sendActivityRequest();
      this.storeNewLastActivity();
    } else {
      this.verifyLastActivity(lastActivity);
    }
  }

  private verifyLastActivity(lastActivity: number): void {
    const hourInMsSec = 3600000;
    const diffHours: number = Math.abs(new Date().getTime() - lastActivity) / hourInMsSec;

    if (diffHours >= 1) {
      this.sendActivityRequest();
      this.storeNewLastActivity();
    }
  }

  private sendActivityRequest(): void {
    if (this.userActivitySubscription) {
      this.userActivitySubscription.unsubscribe();
    }

    this.userActivitySubscription = this.usersBackendService.updateUserActivity().subscribe();
  }

  private storeNewLastActivity(): void {
    this.localStorage.store(CONSTANTS.SESSION.LAST_ACTIVITY, new Date().getTime());
  }
}
